import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';

/**
 * The rules and styling the account forms share.
 *
 * Kept apart from the components in `authUi.tsx` so that file exports nothing
 * but components, which is what lets Fast Refresh keep a form's state while
 * its markup is edited.
 */

/** Lighter than the schedule's conflict red: this is small text on mocha. */
export const ERROR_TEXT = '#ff8a73';

export const fieldSx = {
  '& .MuiOutlinedInput-root': {
    color: BRAND.parchment,
    fontFamily: FONTS.body,
    // Sixteen pixels, not fifteen: iOS Safari zooms the whole page into any
    // focused field smaller than that, and a login form that lurches sideways
    // the moment you tap it reads as broken.
    fontSize: 16,
    '& fieldset': { borderColor: 'rgba(243,238,231,0.3)' },
    '&:hover fieldset': { borderColor: 'rgba(243,238,231,0.5)' },
    '&.Mui-focused fieldset': { borderColor: BRAND.parchment },
    '&.Mui-error fieldset': { borderColor: ERROR_TEXT },
  },
  '& .MuiInputLabel-root': { color: 'rgba(243,238,231,0.6)', fontFamily: FONTS.body, fontSize: 15 },
  '& .MuiInputLabel-root.Mui-focused': { color: BRAND.parchment },
  '& .MuiInputLabel-root.Mui-error': { color: ERROR_TEXT },
  '& .MuiFormHelperText-root': {
    fontFamily: FONTS.body,
    fontSize: 12.5,
    marginLeft: 0,
    color: 'rgba(243,238,231,0.6)',
  },
  '& .MuiFormHelperText-root.Mui-error': { color: ERROR_TEXT },
  // Password managers fill these fields, and browsers paint a filled field pale
  // yellow — on this ground, a bright bar of unreadable text.
  '& input:-webkit-autofill': {
    WebkitBoxShadow: '0 0 0 100px #2c2622 inset',
    WebkitTextFillColor: BRAND.parchment,
    caretColor: BRAND.parchment,
  },
} as const;

/** Deliberately loose: the verification email is the real test of an address. */
export const isValidEmail = (value: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(value.trim());

export const MIN_PASSWORD_LENGTH = 8;

export interface PasswordCheck {
  id: string;
  label: string;
  met: boolean;
}

/**
 * What a new password has to be.
 *
 * Length, and not being the email address — and nothing else. Rules demanding
 * a symbol and a capital make passwords harder to remember without making them
 * harder to guess, which is why current guidance (NIST SP 800-63B) dropped
 * them. A long phrase is the password this should encourage.
 */
export function passwordChecks(password: string, email?: string): PasswordCheck[] {
  const checks: PasswordCheck[] = [
    {
      id: 'length',
      label: `At least ${MIN_PASSWORD_LENGTH} characters`,
      met: password.length >= MIN_PASSWORD_LENGTH,
    },
  ];
  if (email !== undefined) {
    checks.push({
      id: 'not-email',
      label: 'Not the same as your email',
      met: password.length > 0 && password.trim().toLowerCase() !== email.trim().toLowerCase(),
    });
  }
  return checks;
}

/**
 * The address waiting to be verified, for the page that says "we sent it to…".
 *
 * Router state carries it across the navigation, and this carries it across a
 * refresh. Not the URL: an email address in a query string ends up in browser
 * history, server logs and every analytics tool on the page.
 */
const PENDING_EMAIL_KEY = 'mcw.pendingVerificationEmail';

export function rememberPendingEmail(email: string): void {
  try {
    sessionStorage.setItem(PENDING_EMAIL_KEY, email);
  } catch {
    // Storage blocked: the page falls back to not naming the address.
  }
}

export function readPendingEmail(): string {
  try {
    return sessionStorage.getItem(PENDING_EMAIL_KEY) ?? '';
  } catch {
    return '';
  }
}
